import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Overlay} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Overlay';
import {PageId} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {VisibilityEyeComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/visibility/VisibilityEyeComponent';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./OverlayMenuItemRenderer.less');
const cx = classnames.bind(styles);

export interface OverlayMenuItemRendererProps
{
    overlay:Overlay,
    pageOverlayHostId:PageId,
    isHeader?:boolean,
    isActive?:boolean,
    isInPinned?:boolean,
    isSeparator?:boolean,
    className?:string,
}

@observer
export class OverlayMenuItemRenderer extends React.Component<OverlayMenuItemRendererProps, any>
{
    public static defaultProps:Partial<OverlayMenuItemRendererProps> = {
        isHeader: false,
        isActive: false,
        isInPinned: false,
        isSeparator: false
    };

    @bind
    private _handleClick()
    {
        const {overlay, pageOverlayHostId} = this.props;
        controllers.ui.surfaceArea.setActiveOverlay(overlay, pageOverlayHostId);
    }

    @bind
    private _handleRemoveAllOverlaysClick(event:React.MouseEvent<any>):void
    {
        event.stopPropagation();
        const {pageOverlayHostId, isInPinned} = this.props;

        if( isInPinned )
            controllers.ui.surfaceArea.removeAllPinnedOverlaysFromPage(pageOverlayHostId);
        else
            controllers.ui.surfaceArea.removeAllUnpinnedOverlaysFromPage(pageOverlayHostId);
    }

    @bind
    private _handleShowAllOverlaysClick(event:React.MouseEvent<any>):void
    {
        event.stopPropagation();
        const {pageOverlayHostId, isInPinned} = this.props;

        if( isInPinned )
            controllers.ui.surfaceArea.makeAllPinnedOverlaysVisible(pageOverlayHostId);
        else
            controllers.ui.surfaceArea.makeAllUnpinnedOverlaysVisible(pageOverlayHostId);
    }

    @bind
    private _handleHideAllOverlaysClick(event:React.MouseEvent<any>):void
    {
        event.stopPropagation();
        const {pageOverlayHostId, isInPinned} = this.props;

        if( isInPinned )
            controllers.ui.surfaceArea.makeAllPinnedOverlaysHidden(pageOverlayHostId);
        else
            controllers.ui.surfaceArea.makeAllUnpinnedOverlaysHidden(pageOverlayHostId);
    }

    @bind
    private _toggleOverlaysVisibility(event:React.MouseEvent<any>):void
    {
        event.stopPropagation();

        const {overlay} = this.props;
        const {surfaceArea} = controllers.ui;

        if( overlay.isHidden )
            surfaceArea.makeOverlayVisible(overlay);
        else
            surfaceArea.makeOverlayHidden(overlay);

    }

    @bind
    private _handleRemoveOverlayClick(event:React.MouseEvent<any>):void
    {
        event.stopPropagation();

        controllers.ui.surfaceArea.removeOverlay(this.props.overlay);
    }

    private _togglePinOverlay = (event:React.MouseEvent<any>):void =>
    {
        event.stopPropagation();

        controllers.ui.surfaceArea.togglePinOverlay(this.props.overlay);
    };

    @bind
    private _renderAsHeader():JSX.Element
    {
        const {className, isInPinned} = this.props;

        return (
            <Row className={cx('menuItem', 'isHeader', {className: !!className})}>
                <div className={cx('title')}>{isInPinned ? 'PERMANENT OVERLAYS' : 'OVERLAYS'}</div>
                <div className={cx('actions')}>
                    <VisibilityEyeComponent
                        className={cx('visibilityEye', 'actionItem')}
                        value={true}
                        onClick={this._handleShowAllOverlaysClick}
                    />

                    <VisibilityEyeComponent
                        className={cx('visibilityEye', 'actionItem')}
                        value={false}
                        onClick={this._handleHideAllOverlaysClick}
                    />

                    <button
                        className={cx('removeIcon', 'actionItem')}
                        onClick={this._handleRemoveAllOverlaysClick}
                    >
                        <MDIcon
                            name={MDIcons.common.Close}
                            size={MDIconSize.medium16}
                        />
                    </button>
                </div>
            </Row>
        );
    }

    @bind
    private _renderAsOverlayItem():JSX.Element
    {
        const {className, isActive, overlay, isInPinned} = this.props;

        return <Row className={cx('menuItem', {className: !!className, isActive})}
                    onClick={this._handleClick}>
            <button className={cx('pinControl', {isPinned: isInPinned})} onClick={this._togglePinOverlay}>
                <MDIcon
                    className={cx('pinIcon', 'pinControlIcon')}
                    name={MDIcons.common.Pin}
                    size={MDIconSize.medium16}
                />
                <MDIcon
                    className={cx('unpinIcon', 'pinControlIcon')}
                    name={MDIcons.common.Unpin}
                    size={MDIconSize.medium16}
                />
            </button>

            <div className={cx('title')}>{overlay.page.name}</div>

            <div className={cx('actions')}>
                <VisibilityEyeComponent
                    className={cx('visibilityEye', 'actionItem')}
                    value={!overlay.isHidden}
                    onClick={this._toggleOverlaysVisibility}
                />

                <button
                    className={cx('removeIcon', 'actionItem')}
                    onClick={this._handleRemoveOverlayClick}
                >
                    <MDIcon
                        name={MDIcons.common.Close}
                        size={MDIconSize.medium16}
                    />
                </button>
            </div>
        </Row>;
    }

    render()
    {
        const {isHeader, isSeparator} = this.props;

        if( isSeparator )
            return <div className={cx('separator')} />;

        return isHeader
            ? this._renderAsHeader()
            : this._renderAsOverlayItem();
    }
}
